import React, { useEffect, useState } from 'react';
import { getHealth } from '../../lib/api';

export const BUILD_VERSION = 'v1.0.0';

/** Ticks once a second. */
export function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Real backend status from /api/health: 'online' | 'offline' | 'checking'. */
export function useSystemStatus() {
  const [status, setStatus] = useState('checking');
  useEffect(() => {
    let alive = true;
    const probe = () =>
      getHealth()
        .then(() => alive && setStatus('online'))
        .catch(() => alive && setStatus('offline'));
    probe();
    const id = setInterval(probe, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return status;
}

export function formatUtc(date) {
  return date.toISOString().slice(11, 19);
}

/**
 * Telemetry strip — a row of mono KEY · VALUE readouts.
 * Always shows live system status, UTC clock and build; `items` are appended.
 * items: [{ k: 'NODES', v: '3 ACTIVE', tone: 'accent' | 'warn' }]
 */
export default function Telemetry({ items = [], bordered = false, className = '', ...props }) {
  const now = useClock();
  const status = useSystemStatus();

  const statusLabel = status === 'online' ? 'ONLINE' : status === 'offline' ? 'DEGRADED' : 'PROBING';
  const statusTone = status === 'online' ? 'accent' : status === 'offline' ? 'warn' : '';
  const dotClass = status === 'online' ? '' : status === 'offline' ? 'warn' : 'off';

  const all = [
    { k: 'SYS', v: statusLabel, tone: statusTone, dot: dotClass },
    { k: 'UTC', v: formatUtc(now) },
    ...items,
    { k: 'BUILD', v: BUILD_VERSION },
  ];

  return (
    <div className={`hud-ticker ${bordered ? 'hud-ticker-bordered' : ''} ${className}`} {...props}>
      {all.map((item) => (
        <span key={item.k} className="hud-ticker-item">
          {item.dot !== undefined && <span className={`hud-dot status-pulse ${item.dot}`} />}
          <span className="hud-k">{item.k}</span>
          <span className={`hud-v ${item.tone || ''}`}>{item.v}</span>
        </span>
      ))}
    </div>
  );
}
